import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function AddCustomer() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    pin: "",
    // Shirt measurements
    l_shirt: "",
    s_shirt: "",
    h1_shirt: "",
    h2_shirt: "",
    h3_shirt: "",
    h4_shirt: "",
    c_shirt: "",
    w_shirt: "",
    bx_shirt: "",
    loos1_shirt: "",
    loos2_shirt: "",
    loos3_shirt: "",
    n_shirt: "",
    cpoint_shirt: "",
    pocket_shirt: "",
    // Pant measurements
    l_pant: "",
    w_pant: "",
    bx_pant: "",
    f_pant: "",
    l2_pant: "",
    b_pant: "",
    knee_pant: "",
    thigh_pant: "",
    pocket_pant: "",
    fleet_pant: "",
    // Churidar measurements
    l_cd: "",
    s_cd: "",
    h1_cd: "",
    h2_cd: "",
    h3_cd: "",
    h4_cd: "",
    c_cd: "",
    w_cd: "",
    h_cd: "",
    bx_cd: "",
    n_f_cd: "",
    n_b_cd: "",
    notes: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [checkingDuplicates, setCheckingDuplicates] = useState({ phone: false, pin: false });
  const [validFields, setValidFields] = useState({ phone: false, pin: false });

  // Debounced duplicate checking
  const checkDuplicate = async (field, value) => {
    if (!value || value.trim() === '') return;
    
    setCheckingDuplicates(prev => ({ ...prev, [field]: true }));
    
    try {
      const response = await fetch(`${API_URL}/customers.php?all=1`, { credentials: 'include' });
      const customers = await response.json();
      
      const existingCustomer = customers.find(c => c[field] === value);
      
      if (existingCustomer) {
        setErrors(prev => ({
          ...prev,
          [field]: `${field === 'phone' ? 'Phone number' : 'PIN'} already exists and is registered to customer "${existingCustomer.name}".`
        }));
        setValidFields(prev => ({ ...prev, [field]: false }));
      } else {
        // Clear error if no duplicate found
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
        setValidFields(prev => ({ ...prev, [field]: true }));
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    } finally {
      setCheckingDuplicates(prev => ({ ...prev, [field]: false }));
    }
  };

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Create debounced duplicate checkers
  const debouncedCheckPhone = debounce((value) => checkDuplicate('phone', value), 500);
  const debouncedCheckPin = debounce((value) => checkDuplicate('pin', value), 500);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
    
    // Reset valid field status when user starts typing
    if (name === 'phone' || name === 'pin') {
      setValidFields(prev => ({ ...prev, [name]: false }));
    }
    
    // Check for duplicates on phone and PIN fields
    if (name === 'phone' && value.trim()) {
      debouncedCheckPhone(value.trim());
    } else if (name === 'pin' && value.trim()) {
      debouncedCheckPin(value.trim());
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Customer name is required";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.pin.trim()) {
      newErrors.pin = "PIN is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    // Prepare payload (remove email and notes, which are not used in backend)
    const {
      name, phone, address, pin,
      l_shirt, s_shirt, h1_shirt, h2_shirt, h3_shirt, h4_shirt, c_shirt, w_shirt, bx_shirt, loos1_shirt, loos2_shirt, loos3_shirt, n_shirt, cpoint_shirt, pocket_shirt,
      l_pant, w_pant, bx_pant, f_pant, l2_pant, b_pant, knee_pant, thigh_pant, pocket_pant, fleet_pant,
      l_cd, s_cd, h1_cd, h2_cd, h3_cd, h4_cd, c_cd, w_cd, h_cd, bx_cd, n_f_cd, n_b_cd
    } = formData;

    // Convert empty measurement fields to 0
    const measurementFields = [
      'l_shirt', 's_shirt', 'h1_shirt', 'h2_shirt', 'h3_shirt', 'h4_shirt', 'c_shirt', 'w_shirt', 'bx_shirt', 'loos1_shirt', 'loos2_shirt', 'loos3_shirt', 'n_shirt', 'cpoint_shirt', 'pocket_shirt',
      'l_pant', 'w_pant', 'bx_pant', 'f_pant', 'l2_pant', 'b_pant', 'knee_pant', 'thigh_pant', 'pocket_pant', 'fleet_pant',
      'l_cd', 's_cd', 'h1_cd', 'h2_cd', 'h3_cd', 'h4_cd', 'c_cd', 'w_cd', 'h_cd', 'bx_cd', 'n_f_cd', 'n_b_cd'
    ];
    const payload = {
      name, phone, address, pin,
      notes: formData.notes
    };
    measurementFields.forEach(field => {
      payload[field] = formData[field] === '' ? 0 : formData[field];
    });

    try {
      const response = await fetch(`${API_URL}/add_customer.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setConfirmation("Customer added successfully!");
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        // Handle duplicate errors specifically
        if (response.status === 409 && data.duplicate_type) {
          const fieldName = data.duplicate_type === 'phone' ? 'phone' : 'pin';
          const existingCustomer = data.existing_customer;
          setErrors({ 
            [fieldName]: `${data.error}. This ${data.duplicate_type} is already registered to customer "${existingCustomer}".` 
          });
        } else {
          setErrors({ submit: data.error || "Failed to add customer. Please try again." });
        }
      }
    } catch (error) {
      setErrors({ submit: "Failed to add customer. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className="max-w-6xl mx-auto w-full">
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Add New Customer</h1>
          <p className="text-gray-600">Enter customer details and measurements</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter customer's full name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? "border-red-300" : validFields.phone ? "border-green-300" : "border-gray-300"
                  }`}
                    placeholder="Enter phone number"
                  />
                  {checkingDuplicates.phone && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                  {validFields.phone && !checkingDuplicates.phone && !errors.phone && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.address ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter address"
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
              </div>

              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1">
                  PIN *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="pin"
                    name="pin"
                    value={formData.pin}
                    onChange={handleChange}
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.pin ? "border-red-300" : validFields.pin ? "border-green-300" : "border-gray-300"
                  }`}
                    placeholder="Enter PIN"
                  />
                  {checkingDuplicates.pin && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                  {validFields.pin && !checkingDuplicates.pin && !errors.pin && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.pin && <p className="text-red-500 text-sm mt-1">{errors.pin}</p>}
              </div>
            </div>
          </div>

          {/* Shirt Measurements */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shirt Measurements (inches)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div>
                <label htmlFor="l_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  L
                </label>
                <input
                  type="number"
                  id="l_shirt"
                  name="l_shirt"
                  value={formData.l_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="s_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  S
                </label>
                <input
                  type="number"
                  id="s_shirt"
                  name="s_shirt"
                  value={formData.s_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h1_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  H1
                </label>
                <input
                  type="number"
                  id="h1_shirt"
                  name="h1_shirt"
                  value={formData.h1_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h2_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  H2
                </label>
                <input
                  type="number"
                  id="h2_shirt"
                  name="h2_shirt"
                  value={formData.h2_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h3_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  H3
                </label>
                <input
                  type="number"
                  id="h3_shirt"
                  name="h3_shirt"
                  value={formData.h3_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h4_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  H4
                </label>
                <input
                  type="number"
                  id="h4_shirt"
                  name="h4_shirt"
                  value={formData.h4_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="c_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  C
                </label>
                <input
                  type="number"
                  id="c_shirt"
                  name="c_shirt"
                  value={formData.c_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="w_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  W
                </label>
                <input
                  type="number"
                  id="w_shirt"
                  name="w_shirt"
                  value={formData.w_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="bx_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  Bx
                </label>
                <input
                  type="number"
                  id="bx_shirt"
                  name="bx_shirt"
                  value={formData.bx_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="loos1_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  Loos1
                </label>
                <input
                  type="number"
                  id="loos1_shirt"
                  name="loos1_shirt"
                  value={formData.loos1_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="loos2_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  Loos2
                </label>
                <input
                  type="number"
                  id="loos2_shirt"
                  name="loos2_shirt"
                  value={formData.loos2_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="loos3_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  Loos3
                </label>
                <input
                  type="number"
                  id="loos3_shirt"
                  name="loos3_shirt"
                  value={formData.loos3_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="n_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  N
                </label>
                <input
                  type="number"
                  id="n_shirt"
                  name="n_shirt"
                  value={formData.n_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="cpoint_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  CPoint
                </label>
                <input
                  type="number"
                  id="cpoint_shirt"
                  name="cpoint_shirt"
                  value={formData.cpoint_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="pocket_shirt" className="block text-sm font-medium text-gray-700 mb-1">
                  Pocket
                </label>
                <input
                  type="number"
                  id="pocket_shirt"
                  name="pocket_shirt"
                  value={formData.pocket_shirt}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          {/* Pant Measurements */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pant Measurements (inches)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div>
                <label htmlFor="l_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  L
                </label>
                <input
                  type="number"
                  id="l_pant"
                  name="l_pant"
                  value={formData.l_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="w_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  W
                </label>
                <input
                  type="number"
                  id="w_pant"
                  name="w_pant"
                  value={formData.w_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="bx_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  Bx
                </label>
                <input
                  type="number"
                  id="bx_pant"
                  name="bx_pant"
                  value={formData.bx_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="f_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  F
                </label>
                <input
                  type="number"
                  id="f_pant"
                  name="f_pant"
                  value={formData.f_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="l2_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  L
                </label>
                <input
                  type="number"
                  id="l2_pant"
                  name="l2_pant"
                  value={formData.l2_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="b_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  B
                </label>
                <input
                  type="number"
                  id="b_pant"
                  name="b_pant"
                  value={formData.b_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="knee_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  Knee
                </label>
                <input
                  type="number"
                  id="knee_pant"
                  name="knee_pant"
                  value={formData.knee_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="thigh_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  Thigh
                </label>
                <input
                  type="number"
                  id="thigh_pant"
                  name="thigh_pant"
                  value={formData.thigh_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="pocket_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  Pocket
                </label>
                <input
                  type="number"
                  id="pocket_pant"
                  name="pocket_pant"
                  value={formData.pocket_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="fleet_pant" className="block text-sm font-medium text-gray-700 mb-1">
                  Fleet
                </label>
                <input
                  type="number"
                  id="fleet_pant"
                  name="fleet_pant"
                  value={formData.fleet_pant}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          {/* Ladies Measurements */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ladies Measurements (inches)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div>
                <label htmlFor="l_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  L
                </label>
                <input
                  type="number"
                  id="l_cd"
                  name="l_cd"
                  value={formData.l_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="s_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  S
                </label>
                <input
                  type="number"
                  id="s_cd"
                  name="s_cd"
                  value={formData.s_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h1_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  H1
                </label>
                <input
                  type="number"
                  id="h1_cd"
                  name="h1_cd"
                  value={formData.h1_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h2_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  H2
                </label>
                <input
                  type="number"
                  id="h2_cd"
                  name="h2_cd"
                  value={formData.h2_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h3_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  H3
                </label>
                <input
                  type="number"
                  id="h3_cd"
                  name="h3_cd"
                  value={formData.h3_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h4_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  H4
                </label>
                <input
                  type="number"
                  id="h4_cd"
                  name="h4_cd"
                  value={formData.h4_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="c_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  C
                </label>
                <input
                  type="number"
                  id="c_cd"
                  name="c_cd"
                  value={formData.c_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="w_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  W
                </label>
                <input
                  type="number"
                  id="w_cd"
                  name="w_cd"
                  value={formData.w_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="h_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  H
                </label>
                <input
                  type="number"
                  id="h_cd"
                  name="h_cd"
                  value={formData.h_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="bx_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  Bx
                </label>
                <input
                  type="number"
                  id="bx_cd"
                  name="bx_cd"
                  value={formData.bx_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="n_f_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  NF
                </label>
                <input
                  type="number"
                  id="n_f_cd"
                  name="n_f_cd"
                  value={formData.n_f_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>

              <div>
                <label htmlFor="n_b_cd" className="block text-sm font-medium text-gray-700 mb-1">
                  NB
                </label>
                <input
                  type="number"
                  id="n_b_cd"
                  name="n_b_cd"
                  value={formData.n_b_cd}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional notes about the customer..."
            />
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}
          {/* Confirmation Message */}
          {confirmation && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-700 text-sm">{confirmation}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isSubmitting ? "Adding Customer..." : "Add Customer"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 sm:flex-none bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
